
import React, { useState, useEffect } from 'react';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';
import { UserRole, RequestStatus, SchedulingStatus, SubscriptionTier, ChatRequest } from '../types.ts';

const MeetingPrepView = ({ user, onChangeTab }: any) => {
  const [requests, setRequests] = useState<ChatRequest[]>([]);
  const [selectedReq, setSelectedReq] = useState<ChatRequest | null>(null);
  const [prepData, setPrepData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const userTier = user.tier || SubscriptionTier.BASIC;
  const isLocked = userTier !== SubscriptionTier.ADVANCED;

  useEffect(() => {
    const allRequests = dbService.getRequestsForUser(user.id, UserRole.STUDENT);
    const confirmed = allRequests.filter(r => r.schedulingStatus === SchedulingStatus.CONFIRMED && r.status !== RequestStatus.COMPLETED);
    setRequests(confirmed);
  }, [user.id]);

  const handleGeneratePrep = async () => {
    if (!selectedReq) return;
    setIsLoading(true);
    try {
      const studentProfile = dbService.getStudentProfile(user.id);
      const profProfile = dbService.getProfessionalProfile(selectedReq.professionalId);
      const result = await aiService.generateMeetingPrep(studentProfile, profProfile, selectedReq);
      setPrepData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const getPartnerName = (req: ChatRequest) => {
    const prof = dbService.getProfessionalProfile(req.professionalId);
    return prof?.fullName || "Professional";
  };

  return (
    <div className="p-12 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen overflow-y-auto">
        <header className="mb-12">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-600 dark:text-indigo-400 mb-2 block">Meeting Intelligence</span>
          <h1 className="text-5xl font-black text-blue-950 dark:text-white tracking-tighter">Session Architect.</h1>
        </header>

        {isLocked ? (
          <div className="bg-white dark:bg-slate-900 p-20 rounded-[4rem] border border-slate-100 dark:border-slate-800 shadow-4xl text-center flex flex-col items-center animate-in zoom-in-95 duration-500">
             <div className="w-24 h-24 bg-blue-50 dark:bg-indigo-900/20 text-blue-600 dark:text-indigo-400 rounded-[2.5rem] flex items-center justify-center text-4xl mb-10 shadow-inner">
                <i className="fas fa-lock"></i>
             </div>
             <h2 className="text-4xl font-black text-blue-950 dark:text-white tracking-tighter mb-4">Prep Tools Locked.</h2>
             <p className="text-slate-500 dark:text-slate-400 text-lg font-medium max-w-md mb-12 leading-relaxed">
               Advanced members get Sage's <span className="text-blue-600 dark:text-indigo-400 font-black">Relational Intelligence</span> suite—including Reverse Mentorship strategy.
             </p>
             <button 
               onClick={() => onChangeTab('membership')}
               className="px-12 py-5 bg-blue-900 dark:bg-indigo-600 text-white rounded-3xl font-black text-lg hover:bg-blue-800 transition-all shadow-2xl shadow-blue-900/20 hover:-translate-y-1"
             >
                Upgrade to Advanced
             </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="space-y-6">
              <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-2">Upcoming Hub Sessions</h3>
              {requests.length === 0 ? (
                <div className="p-10 bg-white dark:bg-slate-900 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-800 text-center opacity-40 text-xs font-bold text-slate-400">
                  No confirmed meetings yet.
                </div>
              ) : (
                requests.map(req => (
                  <button 
                    key={req.id} 
                    onClick={() => { setSelectedReq(req); setPrepData(null); }}
                    className={`w-full text-left p-6 rounded-[2rem] border transition-all ${selectedReq?.id === req.id ? 'bg-blue-900 dark:bg-indigo-600 text-white border-blue-900 shadow-xl scale-[1.02]' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-blue-200'}`}
                  >
                    <p className="font-black text-lg mb-1">{getPartnerName(req)}</p>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${selectedReq?.id === req.id ? 'text-blue-300' : 'text-slate-400'}`}>
                      {new Date(req.scheduledTime!).toLocaleDateString()} • {new Date(req.scheduledTime!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </p>
                  </button>
                ))
              )}
            </div>

            <div className="lg:col-span-2">
               {!selectedReq ? (
                  <div className="h-full bg-slate-50 dark:bg-slate-900/20 rounded-[3rem] border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center p-20 text-center opacity-40">
                     <i className="fas fa-clipboard-list text-6xl text-slate-300 dark:text-slate-700 mb-6"></i>
                     <p className="font-bold text-slate-400">Select a connection to build your strategic prep guide.</p>
                  </div>
               ) : !prepData ? (
                  <div className="bg-white dark:bg-slate-900 p-12 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-xl text-center flex flex-col items-center justify-center h-full">
                     <h3 className="text-3xl font-black text-blue-950 dark:text-white mb-4 tracking-tighter">Analyze {getPartnerName(selectedReq)}'s Journey.</h3>
                     <p className="text-slate-500 dark:text-slate-400 mb-10 max-w-sm font-medium">Sage will deep-scan history and identify Reverse Mentorship opportunities.</p>
                     <button 
                       onClick={handleGeneratePrep}
                       disabled={isLoading}
                       className="px-10 py-5 bg-blue-950 dark:bg-indigo-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-900 transition shadow-2xl disabled:opacity-50 flex items-center gap-3"
                     >
                       {isLoading ? <i className="fas fa-circle-notch animate-spin"></i> : <i className="fas fa-brain"></i>}
                       {isLoading ? 'Synthesizing Intelligence...' : 'Architect Prep Guide'}
                     </button>
                  </div>
               ) : (
                  <div className="space-y-10 animate-in fade-in slide-in-from-bottom-8 pb-32">
                     
                     <section>
                        <div className="flex items-center gap-3 mb-6">
                           <div className="w-10 h-10 bg-amber-500 text-white rounded-xl flex items-center justify-center shadow-lg"><i className="fas fa-gem text-xs"></i></div>
                           <h3 className="text-xl font-black text-blue-950 dark:text-white tracking-tight">Relational Anchors</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                           {prepData.deepAnchors?.map((anchor: any, i: number) => (
                             <div key={i} className="p-6 bg-gradient-to-br from-amber-50 to-white dark:from-slate-900 dark:to-slate-800 border border-amber-200 dark:border-amber-900/30 rounded-[2rem] shadow-sm group hover:shadow-md transition-all">
                                <div className="flex items-center gap-4 mb-3">
                                   <div className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center text-amber-600 shadow-inner border border-amber-100 dark:border-amber-900 group-hover:scale-110 transition-transform">
                                      <i className={`fas ${anchor.icon || 'fa-link'} text-xs`}></i>
                                   </div>
                                   <p className="text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest">{anchor.title}</p>
                                </div>
                                <p className="text-[13px] font-bold text-blue-950 dark:text-slate-200 leading-relaxed italic">"{anchor.insight}"</p>
                             </div>
                           ))}
                        </div>
                     </section>

                     {/* REVERSE MENTORSHIP SECTION */}
                     <section className="bg-gradient-to-br from-indigo-600 to-indigo-900 text-white p-10 rounded-[3.5rem] shadow-4xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-10 text-[180px] rotate-12 pointer-events-none group-hover:rotate-0 transition-transform duration-1000"><i className="fas fa-exchange-alt"></i></div>
                        <div className="relative z-10">
                           <div className="flex items-center gap-4 mb-8">
                              <span className="bg-white/20 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em] backdrop-blur-md border border-white/10">Feature 03</span>
                              <h3 className="text-2xl font-black tracking-tight uppercase">Reverse Mentorship Engine</h3>
                           </div>
                           
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                              <div className="space-y-4">
                                 <p className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-200">The Blind Spot</p>
                                 <p className="text-lg font-medium leading-relaxed italic border-l-4 border-white/20 pl-6">
                                    "{prepData.reverseMentorship?.professionalBlindSpot}"
                                 </p>
                              </div>
                              <div className="space-y-6">
                                 <div className="bg-white/10 p-6 rounded-3xl border border-white/5 backdrop-blur-md">
                                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-300 mb-3">Your Value Hook</p>
                                    <p className="text-sm font-bold text-white mb-6 leading-relaxed">{prepData.reverseMentorship?.studentValueHook}</p>
                                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-300 mb-2">How to pivot the chat</p>
                                    <p className="text-xs font-medium text-blue-100 leading-relaxed italic opacity-80">"{prepData.reverseMentorship?.howToPitch}"</p>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </section>

                     {/* UPDATED: STRATEGIC QUESTION BANK */}
                     <section className="bg-blue-950 text-white p-12 rounded-[3.5rem] shadow-4xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-900 rounded-full blur-[100px] opacity-30 -translate-y-1/2 translate-x-1/2"></div>
                        <div className="flex items-center gap-4 mb-12 relative z-10">
                           <div className="w-12 h-12 bg-white/10 text-blue-300 rounded-2xl flex items-center justify-center text-xl shadow-inner border border-white/5"><i className="fas fa-question"></i></div>
                           <h3 className="text-2xl font-black tracking-tight">Strategic Question Bank</h3>
                        </div>
                        <div className="space-y-8 relative z-10">
                           {prepData.personalizedImpactQuestions?.map((q: any, i: number) => (
                             <div key={i} className="group bg-white/5 rounded-3xl p-8 border border-white/5 hover:bg-white/10 transition-all">
                                <div className="flex items-start gap-4 mb-4">
                                   <span className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-black shadow-lg shrink-0">Q{i+1}</span>
                                   <p className="text-xl font-bold leading-relaxed text-blue-50 group-hover:text-white transition-colors">"{q.question || q.text}"</p>
                                </div>
                                <div className="pl-12">
                                   <div className="inline-block p-3 bg-black/20 rounded-xl border border-white/5 text-xs text-blue-200 font-medium leading-relaxed italic">
                                      <i className="fas fa-lightbulb text-amber-400 mr-2"></i>
                                      Why this works: {q.psychology}
                                   </div>
                                </div>
                             </div>
                           ))}
                        </div>
                     </section>

                     <section className="bg-white dark:bg-slate-900 p-12 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-2xl">
                        <div className="flex items-center gap-4 mb-10">
                           <div className="w-12 h-12 bg-blue-50 dark:bg-indigo-900/20 text-blue-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center text-xl shadow-sm"><i className="fas fa-stream"></i></div>
                           <h3 className="text-2xl font-black text-blue-950 dark:text-white tracking-tight">Strategic Session Flow</h3>
                        </div>
                        <div className="space-y-8">
                           {prepData.agenda?.map((item: any, i: number) => (
                             <div key={i} className="flex gap-8 group">
                                <div className="flex flex-col items-center">
                                   <div className="w-12 h-12 bg-blue-950 dark:bg-indigo-600 text-white rounded-2xl flex items-center justify-center font-black text-sm shadow-xl z-10 group-hover:bg-blue-600 transition-colors">0{i+1}</div>
                                   {i < prepData.agenda.length - 1 && <div className="w-0.5 flex-1 bg-slate-100 dark:bg-slate-800 my-2"></div>}
                                </div>
                                <div className="flex-1 pb-4">
                                   <div className="flex justify-between items-start mb-2">
                                      <h4 className="text-base font-black text-blue-950 dark:text-white">{item.topic}</h4>
                                      <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800 px-3 py-1 rounded-full uppercase tracking-widest">{item.time}</span>
                                   </div>
                                   <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-relaxed mb-4">{item.details}</p>
                                   <div className="p-3 bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/20 rounded-xl text-[10px] font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                                      <i className="fas fa-bullseye"></i> Relational Goal: {item.relationalGoal}
                                   </div>
                                </div>
                             </div>
                           ))}
                        </div>
                     </section>
                  </div>
               )}
            </div>
          </div>
        )}
    </div>
  );
};

export default MeetingPrepView;
